<template>
  <div class="register-container" :class="{ 'is-embedded': embedded }">
    <div class="content-wrapper">
      <div style="position: relative;display: inline-block;margin-bottom: 30px;">
        <div class="page-title">造价师注册</div>
        <div class="page-title-line" />
      </div>

      <!-- 选择入口 -->
      <div class="form-section">
        <div class="section-label">选择入口</div>
        <div class="entry-buttons">
          <div
            class="entry-btn"
            :class="{ active: entryType === 1 }"
            @click="selectEntryType(1)"
          >
            个人
          </div>
          <div
            class="entry-btn"
            :class="{ active: entryType === 2 }"
            @click="selectEntryType(2)"
          >
            企业
          </div>
        </div>
      </div>

      <el-form
        ref="registerForm"
        :model="registerForm"
        :rules="effectiveRules"
        class="register-form"
      >
        <!-- 手机号 -->
        <el-form-item prop="username">
          <label><span v-if="embedded" class="form-label-required">*</span>手机号</label>
          <el-input
            v-model="registerForm.username"
            placeholder="请输入手机号"
          />
        </el-form-item>

        <!-- 密码 -->
        <el-form-item prop="password">
          <label><span v-if="embedded" class="form-label-required">*</span>密码</label>
          <el-input
            v-model="registerForm.password"
            type="password"
            placeholder="请输入密码"
            show-password
          />
        </el-form-item>

        <!-- 图形验证码 -->
        <el-form-item prop="captcha">
          <label><span v-if="embedded" class="form-label-required">*</span>图形验证码</label>
          <div class="captcha-row">
            <el-input
              v-model="registerForm.captcha"
              placeholder="请输入图形验证码"
              name="captcha"
              auto-complete="off"
            />
            <img
              class="captcha-img"
              :src="captchaUrl"
              alt="图形验证码"
              @click="refreshCaptcha"
            >
          </div>
        </el-form-item>

        <!-- 个人：真实姓名；企业：公司名称（传参仍为 name） -->
        <el-form-item prop="name">
          <label><span v-if="embedded" class="form-label-required">*</span>{{ entryType === 2 ? '公司名称' : '真实姓名' }}</label>
          <el-input
            v-model="registerForm.name"
            :placeholder="entryType === 2 ? '请输入公司名称' : '请输入真实姓名'"
          />
        </el-form-item>

        <template v-if="!embedded">
          <!-- 选择分类（仅个人类型显示） -->
          <div v-if="entryType === 1" class="form-section">
            <div class="section-label">选择分类</div>
            <div class="category-buttons">
              <div
                class="category-btn"
                :class="{ active: categoryType === 1 }"
                @click="selectCategoryType(1)"
              >
                国内/国际
              </div>
              <div
                class="category-btn"
                :class="{ active: categoryType === 2 }"
                @click="selectCategoryType(2)"
              >
                级别
              </div>
              <div
                class="category-btn"
                :class="{ active: categoryType === 3 }"
                @click="selectCategoryType(3)"
              >
                项目分类
              </div>
            </div>
          </div>

          <!-- 专业领域 -->
          <el-form-item prop="professional_field">
            <label>专业领域</label>
            <el-select
              v-model="registerForm.professional_field"
              placeholder="请选择专业领域"
              style="width: 100%;"
              @visible-change="handleProfessionalFieldDropdown"
            >
              <el-option
                v-for="field in professionalFields"
                :key="field.value"
                :label="field.label"
                :value="field.value"
              />
            </el-select>
          </el-form-item>

          <!-- 身份证正反面 -->
          <el-form-item prop="id_card_front">
            <label>身份证正反面</label>
            <div class="upload-area">
              <div class="upload-item">
                <div
                  class="upload-placeholder"
                  :class="{ uploading: uploading }"
                  @click="triggerFileInput('id_card_front')"
                >
                  <img
                    v-if="previewImages.id_card_front"
                    :src="previewImages.id_card_front"
                    alt="身份证正面"
                    class="preview-img"
                  >
                  <div v-else class="upload-content">
                    <i class="el-icon-camera-solid" />
                    <div class="upload-text">上传身份证正面</div>
                  </div>
                  <div v-if="uploading" class="upload-loading">
                    <i class="el-icon-loading" />
                  </div>
                </div>
                <input
                  ref="fileInput_id_card_front"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleImageUpload($event, 'id_card_front')"
                >
              </div>
              <div class="upload-item">
                <div
                  class="upload-placeholder"
                  :class="{ uploading: uploading }"
                  @click="triggerFileInput('id_card_back')"
                >
                  <img
                    v-if="previewImages.id_card_back"
                    :src="previewImages.id_card_back"
                    alt="身份证反面"
                    class="preview-img"
                  >
                  <div v-else class="upload-content">
                    <i class="el-icon-camera-solid" />
                    <div class="upload-text">上传身份证反面</div>
                  </div>
                  <div v-if="uploading" class="upload-loading">
                    <i class="el-icon-loading" />
                  </div>
                </div>
                <input
                  ref="fileInput_id_card_back"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleImageUpload($event, 'id_card_back')"
                >
              </div>
            </div>
          </el-form-item>

          <!-- 职业资格证书 -->
          <el-form-item prop="qualification_cert">
            <label>职业资格证书</label>
            <div class="upload-area">
              <div class="upload-item large">
                <div
                  class="upload-placeholder"
                  :class="{ uploading: uploading }"
                  @click="triggerFileInput('qualification_cert')"
                >
                  <img
                    v-if="previewImages.qualification_cert"
                    :src="previewImages.qualification_cert"
                    alt="资格证书"
                    class="preview-img"
                  >
                  <div v-else class="upload-content">
                    <i class="el-icon-document" />
                    <div class="upload-text">上传职业资格证书</div>
                  </div>
                  <div v-if="uploading" class="upload-loading">
                    <i class="el-icon-loading" />
                  </div>
                </div>
                <input
                  ref="fileInput_qualification_cert"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleImageUpload($event, 'qualification_cert')"
                >
              </div>
            </div>
          </el-form-item>

          <!-- 营业执照（仅企业类型显示） -->
          <el-form-item v-if="entryType === 2" prop="business_license">
            <label>营业执照上传</label>
            <div class="upload-area">
              <div class="upload-item large">
                <div
                  class="upload-placeholder"
                  :class="{ uploading: uploading }"
                  @click="triggerFileInput('business_license')"
                >
                  <img
                    v-if="previewImages.business_license"
                    :src="previewImages.business_license"
                    alt="营业执照"
                    class="preview-img"
                  >
                  <div v-else class="upload-content">
                    <i class="el-icon-office-building" />
                    <div class="upload-text">上传营业执照</div>
                  </div>
                  <div v-if="uploading" class="upload-loading">
                    <i class="el-icon-loading" />
                  </div>
                </div>
                <input
                  ref="fileInput_business_license"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleImageUpload($event, 'business_license')"
                >
              </div>
            </div>
          </el-form-item>

          <!-- 其他联系方式（备用） -->
          <el-form-item>
            <label>其他联系方式（非必填）</label>
            <el-input
              v-model="registerForm.other_contact"
              placeholder="请输入其他联系方式"
            />
          </el-form-item>
        </template>

        <div v-if="embedded && formError" class="inline-form-error">{{ formError }}</div>
        <register-agreement ref="registerAgreement" />
        <!-- 注册按钮 -->
        <el-button
          type="primary"
          class="register-btn"
          :loading="loading"
          @click="handleRegister"
        >
          立即注册
        </el-button>
      </el-form>

      <!-- 返回登录 -->
      <div class="footer-links">
        <span class="link-item" @click="goToLogin">已有账号？去登录</span>
      </div>
    </div>
  </div>
</template>

<script>
import { registerCostEngineer, getProfessionalFields, uploadImage } from '@/api/user'
import RegisterAgreement from '@/components/RegisterAgreement.vue'

export default {
  name: 'EngineerRegister',
  components: { RegisterAgreement },
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const validatePhone = (rule, value, callback) => {
      if (!/^1[3-9]\d{9}$/.test(value)) {
        callback(new Error('请输入正确的手机号'))
      } else {
        callback()
      }
    }
    const validatePassword = (rule, value, callback) => {
      if (!value || value.length < 6) {
        callback(new Error('密码不能少于6位'))
      } else {
        callback()
      }
    }
    const validateCaptcha = (rule, value, callback) => {
      if (!value || value.trim() === '') callback(new Error('请输入图形验证码'))
      else callback()
    }
    const validateRequired = (rule, value, callback) => {
      // 处理字符串类型
      if (typeof value === 'string') {
        if (!value || value.trim() === '') {
          callback(new Error('此字段为必填项'))
          return
        }
      } else if (value === null || value === undefined || value === '') {
        // 处理其他类型（数字、对象等）
        callback(new Error('此字段为必填项'))
        return
      }
      callback()
    }
    return {
      entryType: 1, // 1个人 2企业
      categoryType: 1, // 1国内/国际 2级别 3项目分类
      loading: false,
      uploading: false,
      professionalFields: [],
      previewImages: {
        id_card_front: '',
        id_card_back: '',
        qualification_cert: '',
        business_license: ''
      },
      registerForm: {
        username: '',
        password: '',
        captcha: '',
        name: '',
        professional_field: '',
        id_card_front: '',
        id_card_back: '',
        qualification_cert: '',
        entry_type: 1, // 1个人 2企业
        category_type: 1, // 1国内/国际 2级别 3项目分类
        business_license: '', // 营业执照（企业用）
        other_contact: '' // 其他联系方式（备用）
      },
      registerRules: {
        username: [{ required: true, trigger: 'blur', validator: validatePhone }],
        password: [{ required: true, trigger: 'blur', validator: validatePassword }],
        captcha: [{ required: true, trigger: 'blur', validator: validateCaptcha }],
        name: [{ required: true, trigger: 'blur', validator: validateRequired }],
        professional_field: [{ required: true, trigger: 'change', validator: validateRequired }],
        id_card_front: [{ required: true, trigger: 'change', validator: validateRequired }],
        id_card_back: [{ required: true, trigger: 'change', validator: validateRequired }],
        qualification_cert: [{ required: true, trigger: 'change', validator: validateRequired }],
        business_license: [{ required: false, trigger: 'change' }] // 企业类型时必填，但通过条件验证
      },
      captchaUrl: '',
      captchaObjectUrl: '', // deprecated
      formError: ''
    }
  },
  computed: {
    effectiveRules() {
      if (this.embedded) {
        const { username, password, captcha, name } = this.registerRules
        return { username, password, captcha, name }
      }
      return this.registerRules
    }
  },
  mounted() {
    if (!this.embedded) {
      this.loadProfessionalFields()
    }
    this.refreshCaptcha()
  },
  methods: {
    validateRequiredGeneric(rule, value, callback) {
      if (typeof value === 'string') {
        if (!value || value.trim() === '') {
          callback(new Error('此字段为必填项'))
          return
        }
      } else if (value === null || value === undefined || value === '') {
        callback(new Error('此字段为必填项'))
        return
      }
      callback()
    },
    refreshCaptcha() {
      const t = Date.now()
      this.captchaUrl = `/api/user/captcha?t=${t}`
    },
    // 选择入口类型
    selectEntryType(type) {
      this.entryType = type
      this.registerForm.entry_type = type
      if (this.embedded && type === 1) {
        this.registerForm.business_license = ''
        const prev = this.previewImages.business_license
        if (prev && String(prev).startsWith('blob:')) {
          try { URL.revokeObjectURL(prev) } catch (e) { /* ignore */ }
        }
        this.previewImages.business_license = ''
        this.$nextTick(() => {
          if (this.$refs.registerForm) this.$refs.registerForm.clearValidate(['business_license'])
        })
      }
    },
    // 选择分类类型
    selectCategoryType(type) {
      this.categoryType = type
      this.registerForm.category_type = type
      this.registerForm.professional_field = ''
      this.loadProfessionalFields()
    },
    async loadProfessionalFields() {
      try {
        const response = await getProfessionalFields()
        const isSuccess = response && (
          response.code === 1 ||
          response.code === '1' ||
          response.status === 1 ||
          response.success === true ||
          response.msg === 'success'
        )
        if (!isSuccess) return

        const raw = response.data
        let list = []

        if (Array.isArray(raw)) {
          list = raw
        } else if (raw && Array.isArray(raw.list)) {
          list = raw.list
        } else if (raw && Array.isArray(raw.rows)) {
          list = raw.rows
        } else if (raw && raw.data && Array.isArray(raw.data)) {
          list = raw.data
        } else if (raw && typeof raw === 'object') {
          // 兼容按 category_type 分组的返回：{ "1": [...], "2": [...], "3": [...] }
          const categoryList = raw[String(this.categoryType)] || raw[this.categoryType]
          if (Array.isArray(categoryList)) {
            list = categoryList
          } else {
            const maybeArray = Object.values(raw).find(item => Array.isArray(item))
            if (Array.isArray(maybeArray)) list = maybeArray
          }
        }

        this.professionalFields = list.map((item, index) => {
          const label = item.label || item.name || item.title || item.text || item.professional_field || item.field_name || item.value || `领域${index + 1}`
          const value = item.value || item.id || item.code || item.key || item.field_id || label
          return { label: String(label), value: String(value) }
        })
      } catch (error) {
        console.error('获取专业领域失败:', error)
      }
    },
    handleProfessionalFieldDropdown(visible) {
      if (this.embedded) return
      if (visible && (!Array.isArray(this.professionalFields) || this.professionalFields.length === 0)) {
        this.loadProfessionalFields()
      }
    },
    handleImageUpload(event, fieldName) {
      const file = event.target.files[0]
      if (!file) return

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
      if (!allowedTypes.includes(file.type)) {
        this.$message.error('只支持 JPG、JPEG、PNG 格式的图片')
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        this.$message.error('图片大小不能超过 5MB')
        return
      }

      this.uploadImageFile(file, fieldName)
    },
    async uploadImageFile(file, fieldName) {
      this.uploading = true
      try {
        const response = await uploadImage(file)
        if (response && response.code === 1) {
          this.registerForm[fieldName] = response.data.url || response.data.fullurl || response.data
          this.previewImages[fieldName] = URL.createObjectURL(file)
          this.$message.success('图片上传成功')
          // 触发验证
          this.$refs.registerForm.validateField(fieldName)
        } else {
          this.$message.error(response?.msg || '图片上传失败')
        }
      } catch (error) {
        console.error('图片上传失败:', error)
        this.$message.error('图片上传失败，请重试')
      } finally {
        this.uploading = false
      }
    },
    triggerFileInput(fieldName) {
      this.$refs[`fileInput_${fieldName}`].click()
    },
    handleRegister() {
      if (this.$refs.registerAgreement && !this.$refs.registerAgreement.validate()) {
        return
      }
      this.$refs.registerForm.validate(valid => {
        if (valid) {
          if (this.embedded) this.formError = ''
          this.loading = true
          const entryType = this.registerForm.entry_type || this.entryType || 1
          const phoneFromMobile = (this.registerForm.username || '').trim()
          const submitData = this.embedded
            ? {
              username: this.registerForm.username,
              password: this.registerForm.password,
              captcha: this.registerForm.captcha,
              name: this.registerForm.name,
              phone: phoneFromMobile,
              entry_type: entryType
            }
            : {
              ...this.registerForm,
              entry_type: entryType,
              phone: phoneFromMobile
            }
          const silentOpt = this.embedded ? { silentError: true } : {}
          registerCostEngineer(submitData, silentOpt).then(response => {
            this.loading = false
            if (response && response.code === 1) {
              this.$message.success('注册成功')
              if (this.embedded) {
                this.$emit('registered')
              } else {
                setTimeout(() => {
                  this.$router.push('/login')
                }, 1500)
              }
            } else {
              const msg = response?.msg || '注册失败'
              if (this.embedded) this.formError = msg
              else this.$message.error(msg)
            }
          }).catch(error => {
            this.loading = false
            console.error('注册失败:', error)
            if (this.embedded) {
              this.formError = (error && error.message) || '注册失败，请重试'
            } else {
              this.$message.error('注册失败，请重试')
            }
            this.refreshCaptcha()
          })
        }
      })
    },
    goToLogin() {
      if (this.embedded) {
        this.$emit('switch-login')
      } else {
        this.$router.push('/login')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.register-container {
  min-height: 100vh;
  width: 100%;
  background: url('~@/assets/image/bg3.png') center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 50px 0;

  &.is-embedded {
    min-height: auto;
    background: none;
    padding: 0;
    display: block;
  }
}

.register-container.is-embedded .content-wrapper {
  max-width: none;
  width: 100%;
  margin: 0;
  box-shadow: none;
  border-radius: 12px;
  padding: 16px 12px;
}

.content-wrapper {
  background: #fff;
  border-radius: 20px;
  padding: 40px;
  width: var(--page-inner-ratio, 80%);
  max-width: 800px;
  max-height: calc(100vh - var(--navbar-total-height, 80px) - 40px);
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);

  @supports (height: 100dvh) {
    max-height: calc(100dvh - var(--navbar-total-height, 80px) - 40px);
  }

  .page-title {
    font-size: 28px;
    font-weight: 600;
    color: #111111;
  }

  .page-title-line {
    width: 120px;
    height: 5px;
    background: #0F6FE2;
    border-radius: 3px;
    position: absolute;
    left: 50%;
    bottom: -10px;
    transform: translateX(-50%);
  }

  .form-section {
    margin-bottom: 30px;

    .section-label {
      font-size: 16px;
      font-weight: 600;
      color: #333;
      margin-bottom: 15px;
    }

    .entry-buttons,
    .category-buttons {
      display: flex;
      gap: 15px;

      .entry-btn,
      .category-btn {
        flex: 1;
        height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        border: 2px solid transparent;

        &.active {
          background: #0f6fe2;
          color: #fff;
          border-color: #0f6fe2;
        }

        &:not(.active) {
          background: #ecf2ff;
          color: #666;
          border-color: #ecf2ff;

          &:hover {
            background: #e8e8e8;
            border-color: #0f6fe2;
            color: #0f6fe2;
          }
        }
      }
    }
  }

  .register-form {
    .form-label-required {
      color: #f56c6c;
      margin-right: 2px;
      font-weight: 600;
    }

    .el-form-item {
      label {
        display: block;
        font-size: 14px;
        font-weight: 500;
        color: #333;
        margin-bottom: 8px;
      }
    }

    .captcha-row {
      display: flex;
      align-items: center;
      gap: 12px;

      .captcha-img {
        width: 96px;
        height: 34px;
        border-radius: 6px;
        border: 1px solid #e0e0e0;
        cursor: pointer;
        object-fit: cover;
        flex-shrink: 0;
      }
    }

    .upload-area {
      display: flex;
      gap: 15px;

      .upload-item {
        flex: 1;

        &.large {
          flex: 2;
        }

        .upload-placeholder {
          height: 120px;
          border: 2px dashed #d9d9d9;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;

          &:hover {
            border-color: #0f6fe2;
            background: #f0f8ff;
          }

          &.uploading {
            border-color: #0f6fe2;
            background: #f0f8ff;
            cursor: not-allowed;
          }

          .upload-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;

            i {
              font-size: 24px;
              color: #999;
              margin-bottom: 8px;
            }

            .upload-text {
              font-size: 12px;
              color: #999;
            }
          }

          .preview-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 6px;
          }

          .upload-loading {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;

            i {
              font-size: 20px;
              color: #0f6fe2;
            }
          }
        }
      }
    }

    .inline-form-error {
      margin-bottom: 10px;
      font-size: 13px;
      line-height: 1.45;
      color: #f56c6c;
      text-align: left;
    }

    .register-btn {
      width: 100%;
      height: 48px;
      margin-top: 20px;
      font-size: 16px;
      font-weight: 600;
    }
  }

  .footer-links {
    text-align: center;
    margin-top: 20px;

    .link-item {
      color: #0f6fe2;
      cursor: pointer;
      font-size: 14px;
      transition: all 0.3s ease;

      &:hover {
        color: #0c89f2;
        text-decoration: underline;
      }
    }
  }
}
</style>

